import { useCallback, useRef } from "react";
import { useLocale } from "../i18n";
import { ApiError, api } from "../services/api";
import type { LocalProgress, SessionData } from "../types/assessment";

/**
 * Syncs local assessment state with the backend API.
 * - Creates a session when email is provided (after group 1)
 * - Periodically syncs answers to the backend
 * - Resumes sessions by email
 */
export function useSync() {
  const syncInFlight = useRef(false);
  const locale = useLocale();

  const createSession = useCallback(
    async (progress: LocalProgress): Promise<SessionData | null> => {
      if (!progress.email) return null;
      try {
        return await api.createSession({
          email: progress.email,
          answers: progress.answers,
          currentQuestion: progress.currentQuestion,
          currentGroup: progress.currentGroupIndex,
          locale,
        });
      } catch (err) {
        console.error("Failed to create session:", err);
        return null;
      }
    },
    [locale],
  );

  const syncAnswers = useCallback(
    async (progress: LocalProgress): Promise<void> => {
      if (!progress.sessionId || syncInFlight.current) return;
      syncInFlight.current = true;
      try {
        await api.saveAnswers(progress.sessionId, {
          answers: progress.answers,
          currentQuestion: progress.currentQuestion,
          currentGroup: progress.currentGroupIndex,
        });
      } catch (err) {
        console.error("Failed to sync answers:", err);
      } finally {
        syncInFlight.current = false;
      }
    },
    [],
  );

  const resumeByEmail = useCallback(
    async (email: string): Promise<SessionData | null> => {
      try {
        return await api.resumeSession(email);
      } catch (err) {
        if (
          err instanceof ApiError &&
          (err.status === 404 || err.status === 410)
        ) {
          return null;
        }
        console.error("Failed to resume session:", err);
        return null;
      }
    },
    [],
  );

  const updateCompany = useCallback(
    async (
      sessionId: string,
      companyName: string,
    ): Promise<SessionData | null> => {
      try {
        return await api.updateCompany(sessionId, companyName);
      } catch (err) {
        console.error("Failed to update company:", err);
        return null;
      }
    },
    [],
  );

  const fetchSession = useCallback(
    async (sessionId: string): Promise<SessionData | null> => {
      try {
        return await api.getSession(sessionId);
      } catch (err) {
        console.error("Failed to fetch session:", err);
        return null;
      }
    },
    [],
  );

  const fetchResults = useCallback(async (sessionId: string) => {
    try {
      return await api.getResults(sessionId);
    } catch (err) {
      console.error("Failed to fetch results:", err);
      return null;
    }
  }, []);

  const pauseSession = useCallback(
    async (sessionId: string, resumeLink: string): Promise<void> => {
      try {
        await api.pauseSession(sessionId, resumeLink);
      } catch (err) {
        console.error("Failed to send pause email:", err);
      }
    },
    [],
  );

  return {
    createSession,
    syncAnswers,
    resumeByEmail,
    updateCompany,
    fetchSession,
    fetchResults,
    pauseSession,
  };
}
